/**
 * The language offer on server-rendered static pages (landing, pricing,
 * chart-type pages), which have no React app. Same rules as the app's
 * LanguageSuggestionView: it only offers, and both answers go through
 * `/language`, which saves the choice and reloads the page in it.
 *
 * templates/sections/language-banner.eta renders the offer complete when
 * the request already said what to offer (Accept-Language, location), so
 * nothing shifts after load; this script then only handles closing it.
 * Otherwise the markup is hidden (the landing stylesheet only knows classes
 * used in templates) and this fills it in if the keyboard layout suggests a
 * language.
 */
import { detectSuggestedLanguage } from "../shared/i18n/languageDetection";
import {
  isLanguage,
  Language,
  LANGUAGE_SUGGESTION_SNOOZE_COOKIE,
  LANGUAGE_SUGGESTION_SNOOZE_DAYS,
} from "../shared/i18n/languages";
import { SUGGESTION_STRINGS } from "../shared/i18n/suggestionStrings";
import {
  readBrowserLanguages,
  readKeyboardLanguages,
} from "./services/languageSignals";

interface OfferData {
  current: Language;
  country: string | null;
  /** Set when the server already rendered the offer. */
  suggested: Language | null;
}

function readOfferData(): OfferData | null {
  const element = document.getElementById("language-offer-data");
  try {
    const data = JSON.parse(element?.textContent || "null");
    return data && isLanguage(data.current) ? data : null;
  } catch {
    return null;
  }
}

function isSnoozed(): boolean {
  return document.cookie
    .split("; ")
    .some((entry) => entry.startsWith(`${LANGUAGE_SUGGESTION_SNOOZE_COOKIE}=`));
}

function snooze(language: Language): void {
  const maxAge = LANGUAGE_SUGGESTION_SNOOZE_DAYS * 24 * 60 * 60;
  document.cookie = `${LANGUAGE_SUGGESTION_SNOOZE_COOKIE}=${language}; max-age=${maxAge}; path=/; samesite=lax`;
}

function switchUrl(language: Language): string {
  const next = `${location.pathname}${location.search}`;
  return `/language?set=${encodeURIComponent(
    language,
  )}&next=${encodeURIComponent(next)}`;
}

function setText(root: Element, name: string, text: string, lang: Language) {
  const element = root.querySelector<HTMLElement>(`[data-offer=${name}]`);
  if (element) {
    element.textContent = text;
    element.lang = lang;
  }
}

function wireClose(root: HTMLElement, language: Language, label: string) {
  const close = root.querySelector<HTMLButtonElement>("[data-offer=close]");
  if (!close) {
    return;
  }
  close.setAttribute("aria-label", label);
  close.title = label;
  close.addEventListener("click", () => {
    snooze(language);
    root.hidden = true;
  });
}

async function showOffer(root: HTMLElement, data: OfferData): Promise<void> {
  const language = detectSuggestedLanguage(
    {
      browserLanguages: readBrowserLanguages(),
      keyboardLanguages: await readKeyboardLanguages(),
      country: data.country,
    },
    data.current,
  );
  if (!language) {
    return;
  }
  const offer = SUGGESTION_STRINGS[language];
  const own = SUGGESTION_STRINGS[data.current];
  setText(root, "message", offer.message, language);
  setText(root, "switch", offer.switchTo, language);
  setText(root, "keep", own.keep, data.current);
  root
    .querySelector("[data-offer=switch]")
    ?.setAttribute("href", switchUrl(language));
  root
    .querySelector("[data-offer=keep]")
    ?.setAttribute("href", switchUrl(data.current));
  wireClose(root, language, own.close);
  root.hidden = false;
}

function init(): void {
  const root = document.querySelector<HTMLElement>("[data-language-offer]");
  const data = readOfferData();
  if (!root || !data) {
    return;
  }
  if (data.suggested && isLanguage(data.suggested)) {
    wireClose(root, data.suggested, SUGGESTION_STRINGS[data.current].close);
    return;
  }
  if (!isSnoozed()) {
    void showOffer(root, data);
  }
}

init();
